import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { RowData } from '@tanstack/react-table';

import { cn } from '@/lib/utils';

import DataTable, { DataTableProps } from '../DataTable';
import DataTableViewOptions from '../DataTable/DataTableViewOptions';
import DataTablePagination from '../DataTable/DataTablePagination';

const dataTableVariants = cva('', {
  variants: {
    variant: {
      default: '',
      card: 'rounded-md border bg-white',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const headerVariants = cva(
  'flex min-w-0 flex-wrap items-center justify-between gap-2',
  {
    variants: {
      variant: {
        default: 'px-4',
        card: '',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

const footerVariants = cva('', {
  variants: {
    variant: {
      default: 'px-4 pb-4',
      card: '',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

interface IDataGridProps<TData extends RowData>
  extends DataTableProps<TData>,
    VariantProps<typeof dataTableVariants> {
  className?: string;
  classNameHeader?: string;
  classNameActions?: string;
  classNameContainer?: string;
  classNameFooter?: string;
  toolbar?: React.ReactNode;
  actions?: React.ReactNode;

  disablePagination?: boolean;

  // emptyState: {
  //   title: React.ReactNode;
  //   actionDescription?: React.ReactNode;
  // };
}

const DataGrid = <TData extends RowData>({
  className,
  classNameContainer,
  classNameHeader,
  classNameActions,
  classNameFooter,
  actions,
  toolbar,
  variant,
  disablePagination,
  ...props
}: IDataGridProps<TData>) => (
  <div className={cn('flex min-w-0 flex-col gap-4', classNameContainer)}>
    <div
      className={headerVariants({
        className: classNameHeader,
        variant,
      })}
    >
      {toolbar}
      <div
        className={cn(
          'flex w-full items-center justify-end gap-2 sm:w-auto',
          classNameActions,
        )}
      >
        <DataTableViewOptions table={props.table} />
        {actions}
      </div>
    </div>

    <div className={dataTableVariants({ className, variant })}>
      <DataTable {...props} />
    </div>

    {!disablePagination && (
      <div className={footerVariants({ className: classNameFooter, variant })}>
        <DataTablePagination table={props.table} className="" />
      </div>
    )}
  </div>
);

export default DataGrid;
